import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, Users, Trophy, Settings, Mail, Clock, CreditCard,
  Plus, Trash2, Save, Eye, Search, RefreshCw, Wrench,
  Image as ImageIcon, Handshake, UserSquare2, Pencil, ShieldCheck, FileText, Upload, Download,
  BarChart3, TrendingUp, Globe, Smartphone, Monitor, Film, Play, Megaphone, Gift
} from "lucide-react";
import { adminApi, handleAdminAuthError, LOGO_URL, resolveImageUrl } from "../lib/config";

function Tabs({ tab, setTab }) {
  const items = [
    { id: "iscritti", label: "Iscritti", Icon: Users },
    { id: "classifiche", label: "Classifiche", Icon: Trophy },
    { id: "statistiche", label: "Statistiche", Icon: BarChart3 },
    { id: "gallery", label: "Gallery", Icon: ImageIcon },
    { id: "premi", label: "Premi", Icon: Gift },
    { id: "pubblicita", label: "Pubblicità", Icon: Megaphone },
    { id: "trailer", label: "Trailer", Icon: Film },
    { id: "regolamento", label: "Regolamento", Icon: FileText },
    { id: "sponsor", label: "Sponsor", Icon: Handshake },
    { id: "fondatori", label: "Fondatori", Icon: UserSquare2 },
    { id: "messaggi", label: "Messaggi", Icon: Mail },
    { id: "consensi", label: "Consensi", Icon: ShieldCheck },
    { id: "impostazioni", label: "Impostazioni", Icon: Settings },
  ];
  return (
    <div className="flex flex-wrap gap-2 mb-8">
      {items.map((i) => {
        const Icon = i.Icon;
        return (
          <button
            key={i.id}
            onClick={() => setTab(i.id)}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-heading font-semibold tracking-wider uppercase transition-all ${
              tab === i.id
                ? "bg-fp-cyan text-fp-bg shadow-[0_0_20px_rgba(0,229,255,0.4)]"
                : "bg-fp-surface/60 border border-white/10 text-white/70 hover:border-fp-cyan/50 hover:text-fp-cyan"
            }`}
            data-testid={`admin-tab-${i.id}`}
          >
            <Icon size={16} /> {i.label}
          </button>
        );
      })}
    </div>
  );
}

function Iscritti() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [manualOpen, setManualOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        adminApi().get("/admin/registrations"),
        adminApi().get("/admin/pending"),
      ]);
      setItems(a.data || []);
      setPending(b.data || []);
    } catch (e) { toast.error("Errore caricamento"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Eliminare l'iscritto?")) return;
    try { await adminApi().delete(`/admin/registrations/${id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const confirmPending = async (sessionId, force = false) => {
    try {
      const r = await adminApi().post("/admin/payments/manual-confirm", { session_id: sessionId, force });
      if (r.data.success) {
        toast.success(`Iscritto confermato! Codice: ${r.data.participant_code}`);
        load();
      } else if (r.data.stripe_paid === false) {
        if (window.confirm(`Stripe non conferma il pagamento.\nErrore: ${r.data.stripe_error || "n/d"}\n\nVuoi confermare comunque manualmente (es. dopo verifica diretta sul dashboard Stripe)?`)) {
          confirmPending(sessionId, true);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore conferma");
    }
  };

  const filtered = items.filter((i) => {
    const s = `${i.nome} ${i.cognome} ${i.email} ${i.telefono}`.toLowerCase();
    return !q || s.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Iscritti paganti" value={items.length} accent />
        <Stat label="In attesa" value={pending.length} />
        <Stat label="Obiettivo" value="1000" />
      </div>

      {/* PENDING (orphan transactions) */}
      {pending.length > 0 && (
        <div className="p-6 md:p-8 rounded-3xl border border-yellow-500/30 bg-yellow-500/5" data-testid="pending-section">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="font-heading font-bold text-xl flex-1 text-yellow-200">⚠️ Pagamenti in attesa di conferma ({pending.length})</h3>
          </div>
          <p className="text-sm text-yellow-100/80 mb-4">
            Questi utenti hanno iniziato il pagamento ma il webhook Stripe non l'ha confermato. Controlla su Stripe se il pagamento è arrivato e clicca <strong>"Conferma"</strong> per registrarli con codice univoco.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-table">
              <thead className="text-yellow-200/60 text-[10px] tracking-[0.3em] uppercase font-heading">
                <tr className="border-b border-yellow-500/20">
                  <th className="text-left px-3 py-2">Nome</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Telefono</th>
                  <th className="text-left px-3 py-2">Session ID</th>
                  <th className="text-left px-3 py-2">Quando</th>
                  <th className="text-right px-3 py-2">Azione</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((it) => (
                  <tr key={it.session_id} className="border-b border-yellow-500/10">
                    <td className="px-3 py-3 font-heading font-semibold">{it.nome} {it.cognome}</td>
                    <td className="px-3 py-3 text-white/70 text-xs">{it.email}</td>
                    <td className="px-3 py-3 text-white/70 text-xs font-mono">{it.telefono || "—"}</td>
                    <td className="px-3 py-3 text-white/40 text-[10px] font-mono">{(it.session_id || "").slice(0, 24)}...</td>
                    <td className="px-3 py-3 text-white/50 text-xs">{new Date(it.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => confirmPending(it.session_id)}
                        className="px-3 py-1.5 rounded-lg bg-fp-cyan text-fp-bg font-heading font-bold text-xs uppercase tracking-wider hover:shadow-[0_0_15px_rgba(0,229,255,0.4)] transition-all"
                        data-testid={`confirm-pending-${it.session_id}`}
                      >
                        Conferma
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
          <h3 className="font-heading font-bold text-xl flex-1">Iscritti paganti</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca..."
              className="pl-9 pr-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white text-sm"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-fp-cyan/40 text-fp-cyan text-xs font-heading font-bold uppercase tracking-wider hover:bg-fp-cyan/10"
            data-testid="manual-add-btn"
          >
            <Plus size={14} /> Aggiungi manuale
          </button>
          <button onClick={load} className="p-2 rounded-lg border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan">
            <RefreshCw size={16} />
          </button>
        </div>
        {loading ? <div className="text-white/50 text-center py-6">Caricamento...</div>
          : filtered.length === 0 ? <div className="text-white/50 text-center py-10">Nessun iscritto</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="paid-users-table">
              <thead className="text-white/50 text-[10px] tracking-[0.3em] uppercase font-heading">
                <tr className="border-b border-white/10">
                  <th className="text-left px-3 py-3">Nome</th>
                  <th className="text-left px-3 py-3">Email</th>
                  <th className="text-left px-3 py-3">Telefono</th>
                  <th className="text-left px-3 py-3">Codice</th>
                  <th className="text-left px-3 py-3">Data</th>
                  <th className="text-right px-3 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-fp-cyan/5">
                    <td className="px-3 py-3 font-heading font-semibold">
                      {it.nome} {it.cognome}
                      {it.manual_entry && <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 text-[9px] uppercase font-heading">manuale</span>}
                      {it.manually_confirmed_by && !it.manual_entry && <span className="ml-2 px-1.5 py-0.5 rounded bg-fp-cyan/20 text-fp-cyan text-[9px] uppercase font-heading">conf. manuale</span>}
                    </td>
                    <td className="px-3 py-3 text-white/70">{it.email}</td>
                    <td className="px-3 py-3 text-white/70">{it.telefono}</td>
                    <td className="px-3 py-3"><span className="px-2 py-1 rounded-md bg-fp-cyan/10 text-fp-cyan font-mono font-bold text-xs">{it.participant_code || "—"}</span></td>
                    <td className="px-3 py-3 text-white/50 text-xs">{new Date(it.paid_at).toLocaleString("it-IT")}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => del(it.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`delete-user-${it.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manualOpen && <ManualAddModal onClose={() => setManualOpen(false)} onSaved={() => { setManualOpen(false); load(); }} />}
    </div>
  );
}

function ManualAddModal({ onClose, onSaved }) {
  const [mode, setMode] = useState("session"); // "session" or "manual"
  const [sessionId, setSessionId] = useState("");
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = mode === "session"
        ? { session_id: sessionId.trim() }
        : { ...form, force: true };
      const r = await adminApi().post("/admin/payments/manual-confirm", payload);
      if (r.data.success) {
        toast.success(`Iscritto! Codice: ${r.data.participant_code}`);
        onSaved();
      } else {
        if (window.confirm(`Stripe non conferma. ${r.data.stripe_error || ""}\n\nForzare conferma manuale?`)) {
          const r2 = await adminApi().post("/admin/payments/manual-confirm", { session_id: sessionId.trim(), force: true });
          if (r2.data.success) { toast.success(`Confermato manualmente. Codice: ${r2.data.participant_code}`); onSaved(); }
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose} data-testid="manual-add-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-3xl border border-white/10 bg-fp-surface p-7">
        <h3 className="font-heading font-bold text-2xl">Aggiungi iscritto manualmente</h3>
        <p className="text-sm text-white/60 mt-2">Usa questa funzione quando il pagamento è arrivato ma non è stato registrato automaticamente.</p>

        <div className="mt-5 flex gap-2">
          <button onClick={() => setMode("session")} className={`flex-1 px-4 py-2 rounded-lg text-sm font-heading font-bold uppercase tracking-wider ${mode === "session" ? "bg-fp-cyan text-fp-bg" : "border border-white/10 text-white/60"}`}>
            Da Session ID Stripe
          </button>
          <button onClick={() => setMode("manual")} className={`flex-1 px-4 py-2 rounded-lg text-sm font-heading font-bold uppercase tracking-wider ${mode === "manual" ? "bg-fp-cyan text-fp-bg" : "border border-white/10 text-white/60"}`}>
            Bonifico / Manuale
          </button>
        </div>

        {mode === "session" ? (
          <div className="mt-5">
            <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Session ID Stripe</label>
            <input type="text" value={sessionId} onChange={(e) => setSessionId(e.target.value)} placeholder="cs_live_..."
              className="mt-2 w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white font-mono text-sm" data-testid="manual-session-id" />
            <p className="mt-2 text-xs text-white/50">Trovi il session_id nella tabella "Pagamenti in attesa" oppure su dashboard.stripe.com → Payments → ID inizia con <code className="text-fp-cyan">cs_live_</code></p>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3">
            <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome" className="px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" />
            <input type="text" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} placeholder="Cognome" className="px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" />
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="col-span-2 px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" />
            <input type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Telefono (opzionale)" className="col-span-2 px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" />
          </div>
        )}

        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-sm">Annulla</button>
          <button onClick={submit} disabled={loading || (mode === "session" ? !sessionId : !form.nome || !form.email)}
            className="px-5 py-2 rounded-lg bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40">
            {loading ? "Conferma..." : "Conferma e genera codice"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`p-6 rounded-2xl border ${accent ? "border-fp-cyan/40 bg-fp-cyan/5" : "border-white/10 bg-fp-surface/50"}`}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">{label}</div>
      <div className={`mt-2 font-heading font-black text-4xl ${accent ? "cyan-gradient" : "silver-gradient"}`}>{value}</div>
    </div>
  );
}

const LEAGUES_UI = [
  { id: "main", label: "Serie A", color: "#00E5FF" },
  { id: "autumn", label: "Autumn League", color: "#F59E0B" },
  { id: "winter", label: "Winter League", color: "#60A5FA" },
  { id: "spring", label: "Spring League", color: "#A78BFA" },
  { id: "summer", label: "Summer League", color: "#FB923C" },
  { id: "champions", label: "Champions League", color: "#00E5FF" },
  { id: "winter_champ", label: "Campione d'Inverno", color: "#FBBF24" },
  { id: "highlander1", label: "Highlander 1", color: "#EF4444" },
  { id: "highlander2", label: "Highlander 2", color: "#EC4899" },
  { id: "return", label: "Fanta Partenope Return", color: "#10B981" },
];

function Classifiche() {
  const [uploads, setUploads] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null); // for upload/edit modal

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/rankings/pdf/list");
      const map = {};
      (r.data || []).forEach((d) => { map[d.league_id] = d; });
      setUploads(map);
    } catch { toast.error("Errore caricamento classifiche"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (league_id) => {
    if (!window.confirm("Eliminare la classifica per questa competizione?")) return;
    try {
      await adminApi().delete(`/admin/rankings/pdf/${league_id}`);
      toast.success("Classifica rimossa");
      load();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h2 className="font-heading font-black text-2xl md:text-3xl silver-gradient mb-2">Classifiche live</h2>
        <p className="text-sm text-white/60">
          Carica un file (PDF, PNG, JPEG, WEBP · max 20 MB) con la classifica ufficiale per ogni competizione.
          Il file sarà visibile e scaricabile dagli utenti nella sezione "Classifiche Live" del sito.
          Per aggiornare la classifica, ricarica il file: sostituirà automaticamente il precedente.
        </p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-white/50">Caricamento...</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {LEAGUES_UI.map((l) => {
            const meta = uploads[l.id];
            return (
              <RankingLeagueCard
                key={l.id}
                league={l}
                meta={meta}
                onUploadClick={() => setSelectedLeague(l)}
                onRemove={() => remove(l.id)}
              />
            );
          })}
        </div>
      )}

      {selectedLeague && (
        <RankingUploadModal
          league={selectedLeague}
          existing={uploads[selectedLeague.id]}
          onClose={() => setSelectedLeague(null)}
          onSaved={() => { setSelectedLeague(null); load(); }}
        />
      )}
    </div>
  );
}

function formatBytesFP(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function RankingLeagueCard({ league, meta, onUploadClick, onRemove }) {
  const dlUrl = meta ? `${adminApi().defaults.baseURL}/rankings/pdf/${league.id}/download` : null;
  return (
    <div
      className={`p-5 rounded-3xl border transition-all ${
        meta
          ? "border-fp-cyan/30 bg-fp-cyan/5"
          : "border-white/10 bg-fp-surface/40 hover:border-white/25"
      }`}
      data-testid={`admin-ranking-card-${league.id}`}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: `${league.color}20`, border: `1px solid ${league.color}66`, color: league.color }}
        >
          <Trophy size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-base">{league.label}</h3>
          {meta ? (
            <>
              <div className="mt-1 text-xs text-white/50 truncate" title={meta.filename}>{meta.filename}</div>
              <div className="mt-1 text-[10px] text-white/40 flex flex-wrap gap-x-2">
                <span>{formatBytesFP(meta.size_bytes)}</span>
                <span>·</span>
                <span className="uppercase">{(meta.mime_type || "").split("/")[1]}</span>
                <span>·</span>
                <span>{meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleDateString("it-IT") : ""}</span>
              </div>
            </>
          ) : (
            <div className="mt-1 text-xs text-white/40">Nessuna classifica caricata</div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {meta && (
          <a
            href={dlUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/15 text-white/80 hover:border-fp-cyan hover:text-fp-cyan text-xs font-heading font-bold uppercase tracking-wider"
            data-testid={`ranking-view-${league.id}`}
          >
            <Eye size={13} /> Apri
          </a>
        )}
        <button
          onClick={onUploadClick}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-fp-cyan text-fp-bg text-xs font-heading font-bold uppercase tracking-wider"
          data-testid={`ranking-upload-${league.id}`}
        >
          <Upload size={13} /> {meta ? "Sostituisci" : "Carica"}
        </button>
        {meta && (
          <button
            onClick={onRemove}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-heading font-bold uppercase tracking-wider"
            data-testid={`ranking-delete-${league.id}`}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>
  );
}

function RankingUploadModal({ league, existing, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState(existing?.description || "");
  const [uploading, setUploading] = useState(false);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("File troppo grande (max 20 MB)"); return; }
    if (!["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      toast.error("Formato non supportato. Ammessi: PDF, PNG, JPEG, WEBP.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result || "").toString().split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await adminApi().post(`/admin/rankings/pdf/${league.id}`, {
        filename: file.name,
        mime_type: file.type,
        file_b64: b64,
        description: description.trim(),
      });
      toast.success(`Classifica ${league.label} caricata!`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose} data-testid="ranking-upload-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-3xl border border-white/10 bg-fp-surface p-7 space-y-5">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center"
            style={{ background: `${league.color}20`, border: `1px solid ${league.color}66`, color: league.color }}
          >
            <Trophy size={20} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Classifica</div>
            <h3 className="font-heading font-bold text-xl silver-gradient">{league.label}</h3>
          </div>
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">File (PDF / immagine — max 20 MB)</label>
          <input
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/webp"
            onChange={onFile}
            disabled={uploading}
            className="mt-2 w-full text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
            data-testid="ranking-file-input"
          />
          {file && <div className="mt-2 text-xs text-white/60 font-mono">{file.name} · {formatBytesFP(file.size)}</div>}
        </div>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Descrizione (opzionale)</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Es. Aggiornata al 15/12/2026"
            maxLength={500}
            className="mt-2 w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="ranking-desc"
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-sm">Annulla</button>
          <button
            onClick={upload}
            disabled={!file || uploading}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
            data-testid="ranking-save-btn"
          >
            <Upload size={14} /> {uploading ? "Caricamento..." : "Pubblica"}
          </button>
        </div>
      </div>
    </div>
  );
}


function Messaggi() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const r = await adminApi().get("/admin/contact-messages"); setItems(r.data || []); } catch {} };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { try { await adminApi().put(`/admin/contact-messages/${id}/read`); load(); } catch {} };
  const del = async (id) => { if (!window.confirm("Eliminare?")) return; try { await adminApi().delete(`/admin/contact-messages/${id}`); load(); } catch {} };
  return (
    <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
      <h3 className="font-heading font-bold text-xl mb-4">Messaggi contatti</h3>
      {items.length === 0 ? <div className="text-white/50 text-center py-6">Nessun messaggio</div> :
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} className={`p-4 rounded-xl border ${m.read ? "border-white/5 bg-fp-bg/30" : "border-fp-cyan/30 bg-fp-cyan/5"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-heading font-semibold">{m.nome}</div>
                  <div className="text-xs text-white/50">{m.email} · {new Date(m.created_at).toLocaleString("it-IT")}</div>
                  <p className="mt-2 text-sm text-white/80">{m.messaggio}</p>
                </div>
                <div className="flex gap-1">
                  {!m.read && <button onClick={() => markRead(m.id)} className="p-2 text-fp-cyan hover:bg-fp-cyan/10 rounded-lg" title="Segna letto"><Eye size={14} /></button>}
                  <button onClick={() => del(m.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

function Statistiche() {
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (p = period) => {
    setLoading(true);
    try {
      const r = await adminApi().get(`/admin/stats?period=${p}`);
      setData(r.data);
    } catch { toast.error("Errore caricamento statistiche"); }
    setLoading(false);
  };
  useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

  const periods = [
    { id: "day", label: "Oggi" },
    { id: "week", label: "7 giorni" },
    { id: "month", label: "30 giorni" },
    { id: "year", label: "12 mesi" },
  ];

  if (!data && loading) {
    return <div className="text-white/50 text-center py-10">Caricamento statistiche...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2 className="font-heading font-black text-2xl md:text-3xl silver-gradient">Statistiche del sito</h2>
            <p className="mt-1 text-sm text-white/60">
              Dati di traffico in tempo reale. Niente cookie, niente tracker di terze parti — solo first-party analytics anonime.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {periods.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-4 py-2 rounded-xl text-xs font-heading font-bold uppercase tracking-wider transition-all ${
                  period === p.id
                    ? "bg-fp-cyan text-fp-bg"
                    : "border border-white/15 text-white/60 hover:border-fp-cyan hover:text-fp-cyan"
                }`}
                data-testid={`stats-period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
            <button onClick={() => load()} className="p-2 rounded-xl border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan" data-testid="stats-refresh">
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Eye} label="Visite totali" value={data?.totals?.visits ?? 0} color="#00E5FF" />
        <KpiCard icon={Users} label="Visitatori unici" value={data?.totals?.uniques ?? 0} color="#A78BFA" />
        <KpiCard icon={TrendingUp} label="Iscritti paganti" value={data?.totals?.paid_signups ?? 0} color="#10B981" />
        <KpiCard icon={BarChart3} label="Tasso conversione" value={`${data?.totals?.conversion_rate ?? 0}%`} color="#FBBF24" />
      </div>

      {/* Time series chart */}
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h3 className="font-heading font-bold text-lg mb-5">Andamento visite</h3>
        <StatsChart data={data?.timeseries || []} />
      </div>

      {/* Breakdown grids */}
      <div className="grid md:grid-cols-2 gap-5">
        <BreakdownCard title="Pagine più viste" icon={Eye} items={data?.top_pages || []} keyLabel="Pagina" testid="stats-top-pages" />
        <BreakdownCard title="Provenienza traffico" icon={Globe} items={data?.top_referrers || []} keyLabel="Sorgente" testid="stats-referrers"
          formatKey={(k) => k === "direct" ? "Diretto / bookmark" : k}
        />
        <BreakdownCard title="Paesi" icon={Globe} items={data?.by_country || []} keyLabel="Paese" testid="stats-countries"
          formatKey={(k) => k === "—" ? "Sconosciuto" : k}
        />
        <BreakdownCard title="Dispositivi" icon={Smartphone} items={data?.by_device || []} keyLabel="Tipo" testid="stats-devices"
          formatKey={(k) => ({ mobile: "📱 Mobile", desktop: "💻 Desktop", tablet: "📱 Tablet", unknown: "Sconosciuto" })[k] || k}
        />
      </div>

      {/* Privacy footnote */}
      <div className="p-5 rounded-2xl border border-white/10 bg-fp-surface/30 text-xs text-white/50">
        <strong className="text-white/70">Note privacy:</strong> i pageview sono salvati first-party (sul tuo server) senza cookie, senza IP completo, senza email.
        Memorizziamo solo: percorso pagina, sorgente (referrer host), Paese (header CDN), tipo dispositivo (categoria), hash User-Agent.
        I dati restano sotto il tuo controllo e non vengono inviati a Google Analytics, Meta, ecc.
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-fp-surface/50 hover:border-fp-cyan/30 transition-all">
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 font-heading font-semibold">{label}</div>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="mt-3 font-heading font-black text-3xl silver-gradient">{value}</div>
    </div>
  );
}

function StatsChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="text-white/40 text-sm text-center py-10">Nessun dato disponibile per questo periodo. Le visite verranno raccolte automaticamente.</div>;
  }
  const max = Math.max(...data.map((d) => d.visits), 1);
  return (
    <div className="overflow-x-auto" data-testid="stats-chart">
      <div className="min-w-[600px] flex items-end gap-2 h-52 px-2">
        {data.map((d) => {
          const h = (d.visits / max) * 100;
          const uh = (d.uniques / max) * 100;
          return (
            <div key={d.label} className="flex-1 flex flex-col items-center group relative">
              <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-fp-bg border border-fp-cyan/30 rounded-lg px-2 py-1 text-[10px] font-mono opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                <div className="text-fp-cyan">{d.visits} visite</div>
                <div className="text-white/60">{d.uniques} unici</div>
              </div>
              <div className="w-full flex items-end justify-center gap-0.5 h-full">
                <div className="w-1/2 bg-fp-cyan/30 hover:bg-fp-cyan/50 rounded-t transition-all" style={{ height: `${h}%` }} />
                <div className="w-1/2 bg-fp-cyan rounded-t transition-all" style={{ height: `${uh}%` }} />
              </div>
              <div className="mt-2 text-[9px] text-white/40 font-mono truncate w-full text-center" title={d.label}>
                {d.label.split(" ")[1] || d.label.split("-").slice(-2).join("/")}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-4 text-xs text-white/60">
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-fp-cyan/40" /> Visite totali</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-fp-cyan" /> Visitatori unici</div>
      </div>
    </div>
  );
}

function BreakdownCard({ title, icon: Icon, items, keyLabel, testid, formatKey }) {
  const total = items.reduce((a, b) => a + (b.count || 0), 0);
  return (
    <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-fp-cyan" />
        <h3 className="font-heading font-bold text-base">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-white/40 text-sm text-center py-6">Nessun dato</div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => {
            const pct = total > 0 ? (it.count / total) * 100 : 0;
            const display = formatKey ? formatKey(it.key) : it.key;
            return (
              <div key={it.key} className="group">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-white/80 truncate flex-1 mr-3" title={it.key}>{display}</span>
                  <span className="text-fp-cyan font-mono">{it.count}</span>
                </div>
                <div className="h-1.5 bg-fp-bg/60 rounded-full overflow-hidden">
                  <div className="h-full bg-fp-cyan transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TrailerAdmin() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/intro-video/meta");
      setMeta(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File troppo grande (max 50 MB). Esportalo in MP4 H.264 a 1080p/30fps per alleggerirlo.");
      return;
    }
    if (!["video/mp4", "video/webm", "video/quicktime", "video/x-matroska", "video/ogg"].includes(f.type)) {
      toast.error("Formato non supportato. Usa MP4, WebM, MOV o MKV.");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    setProgress(0);
    const token = localStorage.getItem("fp_admin_token") || "";
    const baseURL = adminApi().defaults.baseURL;
    const CHUNK_SIZE = 1024 * 1024; // 1 MB per chunk
    const total_chunks = Math.ceil(file.size / CHUNK_SIZE);
    let session_id = null;
    try {
      // 1. init
      const initR = await axios.post(`${baseURL}/admin/intro-video/chunked/init`, {
        filename: file.name,
        mime_type: file.type || "video/mp4",
        total_size: file.size,
        total_chunks,
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        timeout: 30000,
      });
      session_id = initR.data.session_id;

      // 2. upload chunks sequentially
      for (let i = 0; i < total_chunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append("file", blob, `chunk_${i}`);
        await axios.post(
          `${baseURL}/admin/intro-video/chunked/chunk?session_id=${encodeURIComponent(session_id)}&index=${i}`,
          fd,
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 120000,
          }
        );
        setProgress(Math.round(((i + 1) / total_chunks) * 100));
      }

      // 3. finalize
      await axios.post(`${baseURL}/admin/intro-video/chunked/finalize`, { session_id }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        timeout: 300000,
      });

      toast.success("Video caricato!");
      setFile(null);
      const el = document.getElementById("trailer-file-input");
      if (el) el.value = "";
      load();
    } catch (e) {
      // cleanup session on failure
      if (session_id) {
        try {
          await axios.delete(`${baseURL}/admin/intro-video/chunked/${session_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch { /* noop */ }
      }
      toast.error(e?.response?.data?.detail || "Errore durante il caricamento");
      handleAdminAuthError(e).catch(() => {});
    }
    setUploading(false);
    setProgress(0);
  };

  const toggleEnabled = async () => {
    if (!meta?.available) return;
    try {
      await adminApi().put("/admin/intro-video/enabled", { enabled: !meta.enabled });
      toast.success(!meta.enabled ? "Video introduttivo ATTIVATO" : "Video introduttivo DISATTIVATO");
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async () => {
    if (!window.confirm("Eliminare il video? Non sarà più mostrato né all'ingresso né nella sezione Trailer.")) return;
    try {
      await adminApi().delete("/admin/intro-video");
      toast.success("Video eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const streamUrl = `${adminApi().defaults.baseURL}/intro-video/stream?v=${encodeURIComponent(meta?.version || meta?.uploaded_at || "")}`;
  const formatBytes = (b) => !b ? "" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6">
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h2 className="font-heading font-black text-2xl md:text-3xl silver-gradient mb-2">Video introduttivo (Trailer)</h2>
        <p className="text-sm text-white/60">
          Carica un video (MP4, WebM, MOV, MKV — max 50 MB, consigliato MP4 H.264 1080p). Quando è <strong>attivo</strong>, viene mostrato a schermo intero
          appena un utente entra nel sito. L'utente può cliccare "Salta" (solo per la sessione corrente) o "Salta sempre"
          (non lo rivedrà più fino a quando non lo riattiva dalla sezione Trailer).
          Il video è sempre visibile nella sezione "Trailer" del sito, indipendentemente dallo stato Attivo/Disattivo.
        </p>
      </div>

      {loading ? (
        <div className="p-8 text-center text-white/50 rounded-3xl border border-white/10 bg-fp-surface/40">Caricamento...</div>
      ) : meta?.available ? (
        <div className="p-6 md:p-8 rounded-3xl border border-fp-cyan/30 bg-fp-cyan/5 space-y-5" data-testid="trailer-current">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <video src={streamUrl} controls preload="metadata" className="w-full md:w-96 aspect-video bg-black rounded-2xl border border-white/10" data-testid="admin-trailer-preview" />
            <div className="flex-1 min-w-0 space-y-3">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Video corrente</div>
                <h3 className="mt-1 font-heading font-bold text-lg break-all">{meta.filename}</h3>
                <div className="mt-1 text-xs text-white/50 flex flex-wrap gap-x-3 gap-y-1">
                  <span>{formatBytes(meta.size_bytes)}</span>
                  <span>·</span>
                  <span className="uppercase">{(meta.mime_type || "").split("/")[1] || meta.mime_type}</span>
                  <span>·</span>
                  <span>Caricato il {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleString("it-IT") : "—"}</span>
                </div>
              </div>

              <div className={`p-4 rounded-2xl border ${meta.enabled ? "border-green-500/30 bg-green-500/10" : "border-white/10 bg-fp-bg/40"}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${meta.enabled ? "bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.6)]" : "bg-white/30"}`} />
                  <div className="flex-1">
                    <div className="font-heading font-bold text-sm">
                      {meta.enabled ? "ATTIVO — video mostrato all'ingresso" : "DISATTIVATO — video visibile solo in sezione Trailer"}
                    </div>
                    <div className="text-xs text-white/60 mt-0.5">
                      {meta.enabled
                        ? "Ogni nuovo visitatore vedrà il video a schermo intero."
                        : "Nessun visitatore vedrà il video all'ingresso. Rimane accessibile nella sezione 'Trailer'."
                      }
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={toggleEnabled}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-heading font-bold text-sm uppercase tracking-wider transition-all ${
                      meta.enabled
                        ? "border border-red-500/40 text-red-300 hover:bg-red-500/10"
                        : "bg-fp-cyan text-fp-bg hover:shadow-[0_0_20px_rgba(0,229,255,0.4)]"
                    }`}
                    data-testid="trailer-toggle-enabled"
                  >
                    <Play size={14} /> {meta.enabled ? "Disattiva" : "Attiva"}
                  </button>
                  <button
                    onClick={remove}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-heading font-bold uppercase tracking-wider"
                    data-testid="trailer-delete"
                  >
                    <Trash2 size={14} /> Elimina
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 md:p-8 rounded-3xl border border-dashed border-white/15 bg-fp-surface/30 text-center" data-testid="trailer-empty">
          <Film size={32} className="mx-auto text-white/30" />
          <p className="mt-3 text-sm text-white/60">Nessun video caricato. Carica il tuo trailer qui sotto.</p>
        </div>
      )}

      {/* Upload */}
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50 space-y-5">
        <h3 className="font-heading font-bold text-xl">
          {meta?.available ? "Sostituisci con un nuovo video" : "Carica il video"}
        </h3>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">File video (MP4 / WebM / MOV — max 50 MB)</label>
          <div className="mt-2">
            <input
              id="trailer-file-input"
              type="file"
              accept="video/mp4,video/webm,video/quicktime,video/x-matroska,video/ogg"
              onChange={onFile}
              disabled={uploading}
              className="w-full text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
              data-testid="trailer-file-input"
            />
            {file && (
              <div className="mt-3 text-xs text-white/60 font-mono">
                {file.name} · {formatBytes(file.size)}
              </div>
            )}
          </div>
        </div>

        {uploading && (
          <div>
            <div className="text-xs text-white/60 mb-2 font-mono">Caricamento {progress}%</div>
            <div className="h-2 bg-fp-bg/60 rounded-full overflow-hidden">
              <div className="h-full bg-fp-cyan transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={upload}
            disabled={!file || uploading}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
            data-testid="trailer-upload-btn"
          >
            <Upload size={16} /> {uploading ? "Caricamento..." : "Carica e pubblica"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegolamentoAdmin() {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi().get("/regolamento");
      setMeta(r.data);
      setDescription(r.data?.description || "");
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("File troppo grande (max 20 MB)"); return; }
    setFile(f);
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result || "").toString().split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await adminApi().post("/admin/regolamento", {
        filename: file.name,
        mime_type: file.type,
        file_b64: b64,
        description: description.trim(),
      });
      toast.success("Regolamento caricato!");
      setFile(null);
      const fi = document.getElementById("regolamento-file-input");
      if (fi) fi.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    }
    setUploading(false);
  };

  const remove = async () => {
    if (!window.confirm("Eliminare il regolamento? Gli utenti non potranno più scaricarlo.")) return;
    try {
      await adminApi().delete("/admin/regolamento");
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const downloadUrl = `${adminApi().defaults.baseURL}/regolamento/download`;
  const formatBytes = (b) => !b ? "" : b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6">
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h2 className="font-heading font-black text-2xl md:text-3xl silver-gradient mb-2">Regolamento ufficiale</h2>
        <p className="text-sm text-white/60">Carica un file (PDF, PNG, JPEG, WEBP o GIF — max 20 MB) che verrà mostrato e reso scaricabile nella sezione "Regolamento" del sito pubblico.</p>
      </div>

      {/* Current file */}
      {loading ? (
        <div className="p-8 text-center text-white/50 rounded-3xl border border-white/10 bg-fp-surface/40">Caricamento...</div>
      ) : meta?.available ? (
        <div className="p-6 md:p-8 rounded-3xl border border-fp-cyan/30 bg-fp-cyan/5" data-testid="regolamento-current">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-fp-cyan/20 border border-fp-cyan/40 flex items-center justify-center text-fp-cyan">
              <FileText size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">In uso ora</div>
              <h3 className="mt-1 font-heading font-bold text-lg break-all">{meta.filename}</h3>
              <div className="mt-1 text-xs text-white/50 flex flex-wrap gap-x-3 gap-y-1">
                <span>{formatBytes(meta.size_bytes)}</span>
                <span>·</span>
                <span className="uppercase">{(meta.mime_type || "").split("/")[1] || meta.mime_type}</span>
                <span>·</span>
                <span>Caricato il {meta.uploaded_at ? new Date(meta.uploaded_at).toLocaleString("it-IT") : "—"}</span>
              </div>
              {meta.description && <p className="mt-2 text-sm text-white/70 italic">"{meta.description}"</p>}
            </div>
            <div className="flex gap-2">
              <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-white/15 text-white/80 hover:border-fp-cyan hover:text-fp-cyan text-sm font-heading font-bold uppercase tracking-wider"
                data-testid="admin-regolamento-view">
                <Eye size={14} /> Apri
              </a>
              <button onClick={remove}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-heading font-bold uppercase tracking-wider"
                data-testid="admin-regolamento-delete">
                <Trash2 size={14} /> Elimina
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 md:p-8 rounded-3xl border border-dashed border-white/15 bg-fp-surface/30 text-center" data-testid="regolamento-empty">
          <FileText size={32} className="mx-auto text-white/30" />
          <p className="mt-3 text-sm text-white/60">Nessun regolamento caricato. Carica un file qui sotto per renderlo disponibile agli utenti.</p>
        </div>
      )}

      {/* Upload form */}
      <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50 space-y-5">
        <h3 className="font-heading font-bold text-xl">
          {meta?.available ? "Sostituisci con un nuovo file" : "Carica un file"}
        </h3>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">File (PDF / immagine — max 20 MB)</label>
          <div className="mt-2 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <input
              id="regolamento-file-input"
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/jpg,image/webp,image/gif"
              onChange={onFile}
              className="flex-1 text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer"
              data-testid="regolamento-file-input"
            />
            {file && <span className="text-xs text-white/60 font-mono">{file.name} ({formatBytes(file.size)})</span>}
          </div>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Descrizione (opzionale)</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Es. Regolamento ufficiale 4ª edizione 2026/2027"
            maxLength={500}
            className="mt-2 w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="regolamento-description"
          />
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={upload}
            disabled={!file || uploading}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
            data-testid="regolamento-upload-btn"
          >
            <Upload size={16} /> {uploading ? "Caricamento..." : "Carica e pubblica"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Impostazioni() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    try {
      const r = await adminApi().get("/admin/settings");
      setS({ ...r.data, notification_emails: (r.data?.notification_emails || []).join(", ") });
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        countdown_enabled: s.countdown_enabled,
        countdown_target: s.countdown_target,
        notification_emails: (s.notification_emails || "").split(",").map((e) => e.trim()).filter(Boolean),
        gmail_sender: s.gmail_sender || "",
        bank_name: s.bank_name || "",
        bank_iban: s.bank_iban || "",
        bank_causale: s.bank_causale || "",
        paypal_email: s.paypal_email || "",
        maintenance_mode: !!s.maintenance_mode,
        maintenance_message: s.maintenance_message || "",
        registration_amount: Number(s.registration_amount) || 60,
        registrations_open: s.registrations_open !== false,
        registrations_closed_message: s.registrations_closed_message || "",
      };
      if (s.gmail_app_password) payload.gmail_app_password = s.gmail_app_password;
      if (newPw) payload.admin_password = newPw;
      await adminApi().put("/admin/settings", payload);
      toast.success("Impostazioni salvate");
      setNewPw("");
      load();
    } catch (e) { toast.error("Errore salvataggio"); }
    setLoading(false);
  };

  if (!s) return <div className="text-white/50 text-center py-10">Caricamento...</div>;

  const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <div className="space-y-6">
      {/* Maintenance Mode */}
      <Section icon={Wrench} title="Modalità manutenzione">
        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-fp-bg/40 border border-white/10 hover:border-fp-cyan/40 transition-colors">
          <input
            type="checkbox"
            checked={!!s.maintenance_mode}
            onChange={(e) => setS({ ...s, maintenance_mode: e.target.checked })}
            className="w-5 h-5 rounded accent-fp-cyan mt-0.5"
            data-testid="toggle-maintenance"
          />
          <div className="flex-1">
            <div className="font-heading font-semibold text-white">
              {s.maintenance_mode ? "🔧 Sito in manutenzione (visibile a tutti)" : "Sito online"}
            </div>
            <div className="text-xs text-white/50 mt-1">
              Quando attivo, i visitatori vedranno solo il logo + il messaggio sotto. L'area admin resta sempre accessibile.
            </div>
          </div>
        </label>
        <Field label="Messaggio mostrato in manutenzione">
          <textarea
            rows={2}
            value={s.maintenance_message || ""}
            onChange={(e) => setS({ ...s, maintenance_message: e.target.value })}
            placeholder="Sito in manutenzione! Torneremo a breve con grandi novità..."
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white resize-none"
            data-testid="maintenance-message-input"
          />
        </Field>
      </Section>

      {/* Registrations open/closed */}
      <Section icon={CreditCard} title="Stato iscrizioni">
        <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl bg-fp-bg/40 border border-white/10 hover:border-fp-cyan/40 transition-colors" data-testid="toggle-registrations-open-label">
          <input
            type="checkbox"
            checked={s.registrations_open !== false}
            onChange={(e) => setS({ ...s, registrations_open: e.target.checked })}
            className="w-5 h-5 rounded accent-fp-cyan mt-0.5"
            data-testid="toggle-registrations-open"
          />
          <div className="flex-1">
            <div className="font-heading font-semibold text-white">
              {s.registrations_open !== false
                ? "Iscrizioni APERTE (i pagamenti e le nuove iscrizioni sono attivi)"
                : "Iscrizioni CHIUSE (form di iscrizione bloccato e pagamenti disabilitati)"}
            </div>
            <div className="text-xs text-white/50 mt-1">
              Quando chiudi le iscrizioni, il pulsante "Iscriviti" sulla home reindirizza a un messaggio informativo, e il form di iscrizione mostra il messaggio configurato qui sotto invece di consentire l'invio del codice OTP o l'avvio del checkout Stripe.
            </div>
          </div>
        </label>
        <Field label="Messaggio mostrato quando le iscrizioni sono chiuse">
          <textarea
            rows={3}
            value={s.registrations_closed_message || ""}
            onChange={(e) => setS({ ...s, registrations_closed_message: e.target.value })}
            placeholder="Le iscrizioni sono momentaneamente chiuse. Torna a trovarci presto..."
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan focus:outline-none text-white resize-none"
            data-testid="registrations-closed-message-input"
          />
        </Field>
      </Section>

      {/* Countdown */}
      <Section icon={Clock} title="Countdown">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!s.countdown_enabled}
            onChange={(e) => setS({ ...s, countdown_enabled: e.target.checked })}
            className="w-5 h-5 rounded accent-fp-cyan"
            data-testid="toggle-countdown"
          />
          <span className="text-sm">Abilita countdown in homepage</span>
        </label>
        <Field label="Data/ora target">
          <input
            type="datetime-local"
            value={toDatetimeLocal(s.countdown_target)}
            onChange={(e) => setS({ ...s, countdown_target: new Date(e.target.value).toISOString() })}
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="countdown-target"
          />
        </Field>
      </Section>

      {/* Email notifications */}
      <Section icon={Mail} title="Email automatiche (codici di verifica, conferme, notifiche)">
        <p className="text-white/60 text-sm">
          Il sito invia le email tramite Brevo. L'email mittente deve essere verificata su Brevo; la Gmail App Password serve solo se Brevo non è configurato.
        </p>
        <Field label="Email destinatari (separate da virgola)">
          <input
            type="text"
            value={s.notification_emails || ""}
            onChange={(e) => setS({ ...s, notification_emails: e.target.value })}
            placeholder="carlo@example.com, tony@example.com"
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="notify-emails"
          />
        </Field>
        <Field label="Email mittente">
          <input
            type="email"
            value={s.gmail_sender || ""}
            onChange={(e) => setS({ ...s, gmail_sender: e.target.value })}
            placeholder="notifiche@gmail.com"
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="gmail-sender"
          />
        </Field>
        <Field label={`Gmail App Password ${s.gmail_app_password_set ? "(impostata — lascia vuoto per mantenere)" : ""}`}>
          <input
            type="password"
            value={s.gmail_app_password || ""}
            onChange={(e) => setS({ ...s, gmail_app_password: e.target.value })}
            placeholder={s.gmail_app_password_set ? "•••••• (già configurata)" : "xxxx xxxx xxxx xxxx"}
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white"
            data-testid="gmail-password"
          />
          <p className="mt-2 text-xs text-white/50">Genera una App Password su myaccount.google.com/apppasswords</p>
        </Field>
      </Section>

      {/* Bank info */}
      <Section icon={CreditCard} title="Dati bonifico e quota">
        <Field label="Quota di iscrizione (EUR)">
          <input
            type="number"
            min="1"
            max="1000"
            step="0.50"
            value={s.registration_amount ?? 60}
            onChange={(e) => setS({ ...s, registration_amount: e.target.value })}
            className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white font-mono"
            data-testid="registration-amount"
          />
          <p className="mt-2 text-xs text-white/50">Importo attualmente fatturato per ogni nuova iscrizione (Stripe + bonifico).</p>
        </Field>
        <Field label="Intestatario">
          <input type="text" value={s.bank_name || ""} onChange={(e) => setS({ ...s, bank_name: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" data-testid="bank-name" />
        </Field>
        <Field label="IBAN">
          <input type="text" value={s.bank_iban || ""} onChange={(e) => setS({ ...s, bank_iban: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white font-mono" data-testid="bank-iban" />
        </Field>
        <Field label="Causale">
          <input type="text" value={s.bank_causale || ""} onChange={(e) => setS({ ...s, bank_causale: e.target.value })} className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" data-testid="bank-causale" />
        </Field>
      </Section>

      {/* 2FA */}
      <TwoFactorAuth />

      {/* Admin password */}
      <Section icon={Settings} title="Cambio password admin">
        <Field label="Nuova password (lascia vuoto per non cambiare)">
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white" data-testid="new-admin-password" />
        </Field>
      </Section>

      <button
        onClick={save}
        disabled={loading}
        className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-50"
        data-testid="save-settings-btn"
      >
        <Save size={16} /> {loading ? "Salvataggio..." : "Salva impostazioni"}
      </button>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-fp-cyan/10 border border-fp-cyan/30 text-fp-cyan flex items-center justify-center"><Icon size={18} /></div>
        <h3 className="font-heading font-bold text-xl">{title}</h3>
      </div>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function TwoFactorAuth() {
  const [status, setStatus] = useState(null); // { enabled }
  const [setup, setSetup] = useState(null); // { secret, qr_code_data_url }
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    try {
      const r = await adminApi().get("/admin/2fa/status");
      setStatus(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const r = await adminApi().post("/admin/2fa/setup", {});
      setSetup(r.data);
    } catch { toast.error("Errore generazione QR"); }
    setLoading(false);
  };

  const enable2fa = async () => {
    if (code.length < 6) { toast.error("Inserisci il codice a 6 cifre"); return; }
    setLoading(true);
    try {
      const r = await adminApi().post("/admin/2fa/enable", { totp_code: code });
      setRecoveryCodes(r.data.recovery_codes || []);
      setSetup(null);
      setCode("");
      toast.success("2FA abilitato!");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Codice errato"); }
    setLoading(false);
  };

  const disable2fa = async () => {
    if (disableCode.length < 6) { toast.error("Inserisci il codice 2FA per disattivare"); return; }
    if (!window.confirm("Disattivare l'autenticazione a due fattori?")) return;
    setLoading(true);
    try {
      await adminApi().post("/admin/2fa/disable", { totp_code: disableCode });
      setDisableCode("");
      setRecoveryCodes(null);
      toast.success("2FA disattivato");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Codice errato"); }
    setLoading(false);
  };

  if (!status) return null;

  return (
    <Section icon={ShieldCheck} title="Autenticazione a due fattori (2FA)">
      {status.enabled && !recoveryCodes && (
        <div className="space-y-4" data-testid="twofa-enabled-block">
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-sm text-green-200">
            <strong>2FA attivo.</strong> A ogni login admin verrà richiesto il codice TOTP.
          </div>
          <Field label="Inserisci il codice attuale per disattivare">
            <div className="flex gap-3">
              <input
                type="text" inputMode="numeric" maxLength={8}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                className="flex-1 px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white font-mono tracking-widest"
                placeholder="000000" data-testid="twofa-disable-input"
              />
              <button onClick={disable2fa} disabled={loading}
                className="px-5 py-3 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-40"
                data-testid="twofa-disable-btn">
                Disattiva
              </button>
            </div>
          </Field>
        </div>
      )}

      {recoveryCodes && (
        <div className="space-y-3 p-4 rounded-xl bg-fp-cyan/5 border border-fp-cyan/30" data-testid="twofa-recovery-codes">
          <div className="text-sm font-heading font-bold text-fp-cyan">⚠️ Codici di recupero — salvali in un luogo sicuro</div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {recoveryCodes.map((c) => <div key={c} className="px-3 py-2 rounded bg-fp-bg/60 border border-white/10">{c}</div>)}
          </div>
          <button onClick={() => setRecoveryCodes(null)} className="text-xs text-fp-cyan hover:underline">Ho salvato i codici</button>
        </div>
      )}

      {!status.enabled && !setup && (
        <div className="space-y-3" data-testid="twofa-disabled-block">
          <p className="text-sm text-white/70">
            Aggiungi un livello di sicurezza in più al tuo account. Avrai bisogno di un'app authenticator
            (Google Authenticator, Authy, 1Password...).
          </p>
          <button onClick={startSetup} disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-fp-cyan/40 text-fp-cyan hover:bg-fp-cyan/10 disabled:opacity-40"
            data-testid="twofa-setup-btn">
            <ShieldCheck size={16} /> Attiva 2FA
          </button>
        </div>
      )}

      {setup && (
        <div className="space-y-4" data-testid="twofa-setup-block">
          <div className="grid sm:grid-cols-[180px_1fr] gap-5 items-start">
            <img src={setup.qr_code_data_url} alt="QR 2FA" className="w-44 h-44 rounded-xl bg-white p-2" data-testid="twofa-qr" />
            <div className="space-y-3 text-sm text-white/75">
              <p>1. Scansiona il QR con la tua app authenticator.</p>
              <p>2. Oppure inserisci manualmente la chiave:
                <span className="block mt-1 font-mono text-xs text-fp-cyan break-all">{setup.secret}</span>
              </p>
              <p>3. Inserisci il codice a 6 cifre per confermare:</p>
            </div>
          </div>
          <div className="flex gap-3">
            <input
              type="text" inputMode="numeric" maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="flex-1 px-4 py-3 rounded-xl bg-fp-bg/60 border border-white/10 focus:border-fp-cyan text-white font-mono tracking-widest text-lg"
              placeholder="000000" data-testid="twofa-enable-input" autoFocus
            />
            <button onClick={enable2fa} disabled={loading || code.length < 6}
              className="px-5 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold disabled:opacity-40"
              data-testid="twofa-enable-btn">
              Conferma
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }}
              className="px-4 py-3 rounded-xl border border-white/10 text-white/60">
              Annulla
            </button>
          </div>
        </div>
      )}
    </Section>
  );
}

function Gallery() {
  const [edition, setEdition] = useState("terza");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", title: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploadingBatch, setUploadingBatch] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });


  const load = async () => {
    try {
      const r = await adminApi().get(`/admin/gallery?edition=${edition}`);
      setItems(r.data || []);
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [edition]);

  const submit = async (e) => {
    e.preventDefault();
    if (!editingId) return;
    try {
      await adminApi().put(`/admin/gallery/${editingId}`, { ...form, edition, order: Number(form.order || 0) });
      toast.success("Aggiornato");
      setForm({ image_url: "", title: "", order: 0 });
      setEditingId(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({ image_url: it.image_url, title: it.title || "", order: it.order || 0 });
  };

  const cancel = () => { setEditingId(null); setForm({ image_url: "", title: "", order: 0 }); setFiles([]); };

  const del = async (id) => {
    if (!window.confirm("Eliminare la foto?")) return;
    try { await adminApi().delete(`/admin/gallery/${id}`); load(); } catch { /* noop */ }
  };

  const onFilesChosen = (e) => {
    const list = Array.from(e.target.files || []);
    const bad = list.filter((f) => !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type));
    if (bad.length > 0) {
      toast.error("Formato non supportato per: " + bad.map((f) => f.name).join(", "));
      return;
    }
    const tooBig = list.filter((f) => f.size > 8 * 1024 * 1024);
    if (tooBig.length > 0) {
      toast.error("File troppo grandi (max 8 MB): " + tooBig.map((f) => f.name).join(", "));
      return;
    }
    setFiles(list);
  };

  const removeFileFromQueue = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveFile = (idx, dir) => {
    setFiles((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const uploadBatch = async () => {
    if (files.length === 0) return;
    setUploadingBatch(true);
    setBatchProgress({ done: 0, total: files.length });
    const maxOrder = items.reduce((m, it) => Math.max(m, it.order || 0), 0);
    let uploaded = 0;
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result || "").toString().split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(f);
        });
        await adminApi().post("/admin/gallery/upload", {
          edition,
          filename: f.name,
          mime_type: f.type,
          image_b64: b64,
          title: f.name.replace(/\.[^.]+$/, "").slice(0, 200),
          order: maxOrder + i + 1,
        }, { timeout: 120000 });
        uploaded += 1;
      } catch (e) {
        failed += 1;
        console.error("upload err", f.name, e);
      }
      setBatchProgress({ done: i + 1, total: files.length });
    }
    setUploadingBatch(false);
    if (failed > 0) toast.error(`${failed} foto non caricate. ${uploaded} caricate correttamente.`);
    else toast.success(`${uploaded} foto caricate!`);
    setFiles([]);
    const el = document.getElementById("gallery-files-input");
    if (el) el.value = "";
    load();
  };

  const formatBytes = (b) => !b ? "" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6">
      <Section icon={ImageIcon} title="Gallery del torneo">
        <p className="text-white/60 text-sm">
          Le foto vengono mostrate nella pagina <code className="text-fp-cyan">/gallery</code> divise per edizione.
          Scegli l'edizione e carica le foto direttamente dal tuo computer: puoi selezionarne tante insieme.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {[{ id: "prima", label: "Prima Edizione" }, { id: "seconda", label: "Seconda Edizione" }, { id: "terza", label: "Terza Edizione" }].map((e) => (
            <button
              key={e.id}
              onClick={() => { setEdition(e.id); cancel(); }}
              className={`px-5 py-2.5 rounded-lg text-sm font-heading font-semibold tracking-wider uppercase transition-all ${
                edition === e.id ? "bg-fp-cyan text-fp-bg" : "border border-white/10 text-white/70 hover:border-fp-cyan/50"
              }`}
              data-testid={`gallery-edition-${e.id}`}
            >
              {e.label}
            </button>
          ))}
        </div>

        {/* Edit form for existing items: title and order */}
        {editingId && (
          <form onSubmit={submit} className="grid md:grid-cols-[64px_2fr_80px_auto] items-center gap-3 mt-5 p-4 rounded-2xl bg-fp-cyan/5 border border-fp-cyan/20">
            <img src={resolveImageUrl(form.image_url)} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Titolo"
              className="px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm"
            />
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })}
              placeholder="Ordine"
              className="px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm"
            />
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 rounded-lg bg-fp-cyan text-fp-bg font-heading font-bold text-sm inline-flex items-center gap-1">
                <Save size={14} /> Salva
              </button>
              <button type="button" onClick={cancel} className="px-3 py-2 rounded-lg border border-white/10 text-white/70 text-sm">Annulla</button>
            </div>
          </form>
        )}
      </Section>

      {/* Multi-file upload section */}
      {!editingId && (
        <div className="p-6 md:p-8 rounded-3xl border border-white/10 bg-fp-surface/50 space-y-5" data-testid="gallery-upload-section">
          <div>
            <h3 className="font-heading font-bold text-xl">Carica foto dal tuo computer</h3>
            <p className="text-sm text-white/60 mt-2">
              Seleziona una o più immagini (JPEG, PNG, WEBP, GIF · max 8 MB ognuna).
              Le foto verranno inserite nell'ordine in cui le disponi qui sotto. Puoi riordinarle o rimuoverne alcune prima di caricare.
            </p>
          </div>

          <div>
            <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Seleziona immagini</label>
            <input
              id="gallery-files-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              multiple
              onChange={onFilesChosen}
              disabled={uploadingBatch}
              className="mt-2 w-full text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
              data-testid="gallery-files-input"
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-2" data-testid="gallery-files-queue">
              <div className="text-xs text-white/60 flex items-center justify-between">
                <span>{files.length} foto pronte da caricare (l'ordine è quello mostrato):</span>
                <button onClick={() => setFiles([])} className="text-red-300 hover:underline">Svuota</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {files.map((f, i) => (
                  <div key={`${f.name}-${i}`} className="relative group rounded-xl overflow-hidden border border-white/10 bg-fp-bg/60" data-testid={`gallery-queue-item-${i}`}>
                    <div className="aspect-square bg-fp-bg flex items-center justify-center overflow-hidden">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute top-1 left-1 px-2 py-1 rounded-full bg-fp-cyan text-fp-bg text-[10px] font-heading font-black">#{i + 1}</div>
                    <button onClick={() => removeFileFromQueue(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid={`gallery-remove-${i}`}>
                      <Trash2 size={12} />
                    </button>
                    <div className="p-2 text-[10px] text-white/70 truncate" title={f.name}>{f.name}</div>
                    <div className="px-2 pb-2 text-[9px] text-white/40 flex items-center justify-between">
                      <span>{formatBytes(f.size)}</span>
                      <div className="flex gap-1">
                        <button onClick={() => moveFile(i, -1)} disabled={i === 0} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↑</button>
                        <button onClick={() => moveFile(i, 1)} disabled={i === files.length - 1} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↓</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {uploadingBatch && (
                <div className="mt-3">
                  <div className="text-xs text-white/60 mb-2 font-mono">
                    Caricamento {batchProgress.done}/{batchProgress.total}
                  </div>
                  <div className="h-2 bg-fp-bg/60 rounded-full overflow-hidden">
                    <div className="h-full bg-fp-cyan transition-all" style={{ width: `${(batchProgress.done / Math.max(batchProgress.total, 1)) * 100}%` }} />
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-3">
                <button
                  onClick={uploadBatch}
                  disabled={uploadingBatch || files.length === 0}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
                  data-testid="gallery-upload-batch-btn"
                >
                  <Upload size={16} /> {uploadingBatch ? "Caricamento..." : `Carica ${files.length} foto`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h3 className="font-heading font-bold text-xl mb-4">Foto attuali · {edition === "prima" ? "Prima Edizione" : edition === "seconda" ? "Seconda Edizione" : "Terza Edizione"}</h3>
        {items.length === 0 ? <div className="text-white/50 text-center py-8">Nessuna foto</div> :
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border border-white/10 bg-fp-bg/40 overflow-hidden">
                <div className="aspect-[4/3] bg-fp-bg overflow-hidden">
                  <img src={resolveImageUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-sm text-white font-heading font-semibold truncate">{it.title || "(senza titolo)"}</div>
                  <div className="text-xs text-white/40">Ordine: {it.order || 0}</div>
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => startEdit(it)} className="flex-1 px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs inline-flex items-center justify-center gap-1" data-testid={`gallery-edit-${it.id}`}>
                      <Pencil size={12} /> Modifica
                    </button>
                    <button onClick={() => del(it.id)} className="px-2 py-1.5 rounded-md text-red-400 hover:bg-red-500/10 text-xs" data-testid={`gallery-delete-${it.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function SponsorAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", title: "", subtitle: "", link_url: "", order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const r = await adminApi().get("/admin/sponsors"); setItems(r.data || []); } catch {}
  };
  useEffect(() => { load(); }, []);

  const uploadImageFile = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"];
    if (!allowed.includes(file.type)) { toast.error("Formato non supportato"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5 MB)"); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res((r.result || "").toString().split(",")[1] || "");
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const resp = await adminApi().post("/admin/sponsors/upload-image", {
        filename: file.name,
        mime_type: file.type,
        image_b64: b64,
      }, { timeout: 60000 });
      setForm((prev) => ({ ...prev, image_url: resp.data.image_url }));
      toast.success("Logo caricato");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore caricamento immagine";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Inserisci almeno il titolo"); return; }
    try {
      const payload = { ...form, order: Number(form.order || 0) };
      if (editingId) {
        await adminApi().put(`/admin/sponsors/${editingId}`, payload);
        toast.success("Aggiornato");
      } else {
        await adminApi().post("/admin/sponsors", payload);
        toast.success("Aggiunto");
      }
      setForm({ image_url: "", title: "", subtitle: "", link_url: "", order: 0 });
      setEditingId(null);
      const el = document.getElementById("sponsor-file-input");
      if (el) el.value = "";
      load();
    } catch { toast.error("Errore"); }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      image_url: it.image_url || "",
      title: it.title || "",
      subtitle: it.subtitle || "",
      link_url: it.link_url || "",
      order: it.order || 0,
    });
  };

  const cancel = () => {
    setEditingId(null);
    setForm({ image_url: "", title: "", subtitle: "", link_url: "", order: 0 });
    const el = document.getElementById("sponsor-file-input");
    if (el) el.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare lo sponsor?")) return;
    try { await adminApi().delete(`/admin/sponsors/${id}`); load(); } catch {}
  };

  return (
    <div className="space-y-6">
      <Section icon={Handshake} title={editingId ? "Modifica sponsor" : "Aggiungi sponsor"}>
        <p className="text-white/60 text-sm">Gli sponsor compaiono nella sezione pubblica "I nostri Sponsor" della homepage. Il logo si carica direttamente dal tuo dispositivo (JPEG/PNG/WEBP/GIF/SVG, max 5 MB).</p>
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Logo / Foto sponsor</label>
            <div className="mt-2 flex flex-col md:flex-row md:items-center gap-3">
              <input
                id="sponsor-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                onChange={(e) => uploadImageFile(e.target.files?.[0])}
                disabled={uploading}
                className="flex-1 text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
                data-testid="sponsor-file-input"
              />
              {form.image_url && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10">
                  <img src={resolveImageUrl(form.image_url)} alt="Anteprima" className="w-12 h-12 object-contain rounded" />
                  <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-red-300 hover:text-red-200 text-xs" data-testid="sponsor-remove-image">
                    Rimuovi
                  </button>
                </div>
              )}
              {uploading && <span className="text-xs text-fp-cyan">Caricamento...</span>}
            </div>
          </div>
          <Field label="Titolo">
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Nome sponsor" className="w-full px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm" data-testid="sponsor-title" />
          </Field>
          <Field label="Sottotitolo">
            <input type="text" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="Descrizione breve" className="w-full px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm" data-testid="sponsor-subtitle" />
          </Field>
          <Field label="Link (opzionale)">
            <input type="url" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="https://sponsor.com" className="w-full px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm" data-testid="sponsor-link" />
          </Field>
          <Field label="Ordine">
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm" data-testid="sponsor-order" />
          </Field>
          <div className="flex items-end gap-2">
            <button type="submit" disabled={uploading} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-fp-cyan text-fp-bg font-heading font-bold text-sm disabled:opacity-50" data-testid="sponsor-submit">
              {editingId ? <><Save size={14} /> Salva</> : <><Plus size={14} /> Aggiungi</>}
            </button>
            {editingId && <button type="button" onClick={cancel} className="px-4 py-2 rounded-lg border border-white/10 text-white/70 text-sm">Annulla</button>}
          </div>
        </form>
      </Section>

      <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h3 className="font-heading font-bold text-xl mb-4">Sponsor attivi</h3>
        {items.length === 0 ? <div className="text-white/50 text-center py-8">Nessuno sponsor</div> :
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((s) => (
              <div key={s.id} className="rounded-xl border border-white/10 bg-fp-bg/40 overflow-hidden">
                {s.image_url && (
                  <div className="aspect-[3/2] bg-white/5 flex items-center justify-center p-3">
                    <img src={resolveImageUrl(s.image_url)} alt={s.title} className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="p-3">
                  <div className="font-heading font-bold text-white truncate">{s.title}</div>
                  {s.subtitle && <div className="text-xs text-white/60 truncate">{s.subtitle}</div>}
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => startEdit(s)} className="flex-1 px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs inline-flex items-center justify-center gap-1" data-testid={`sponsor-edit-${s.id}`}>
                      <Pencil size={12} /> Modifica
                    </button>
                    <button onClick={() => del(s.id)} className="px-2 py-1.5 rounded-md text-red-400 hover:bg-red-500/10 text-xs" data-testid={`sponsor-delete-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function FoundersAdmin() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = async () => {
    try { const r = await adminApi().get("/admin/settings"); setS(r.data); } catch { toast.error("Errore"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await adminApi().put("/admin/settings", {
        founder_carlo_img: s.founder_carlo_img || "",
        founder_antonio_img: s.founder_antonio_img || "",
        founder_tony_img: s.founder_tony_img || "",
        founder_carlo_bio: s.founder_carlo_bio || "",
        founder_antonio_bio: s.founder_antonio_bio || "",
        founder_tony_bio: s.founder_tony_bio || "",
      });
      toast.success("Fondatori aggiornati");
    } catch { toast.error("Errore salvataggio"); }
    setLoading(false);
  };

  const uploadPhoto = async (key, file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowed.includes(file.type)) { toast.error("Formato non supportato"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Immagine troppo grande (max 8 MB)"); return; }
    setUploadingKey(key);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res((r.result || "").toString().split(",")[1] || "");
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const resp = await adminApi().post("/admin/founders/upload-image", {
        filename: file.name,
        mime_type: file.type,
        image_b64: b64,
      }, { timeout: 60000 });
      setS((prev) => ({ ...prev, [`founder_${key}_img`]: resp.data.image_url }));
      toast.success("Foto caricata: ricordati di salvare");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento immagine");
    } finally {
      setUploadingKey(null);
    }
  };

  if (!s) return <div className="text-white/50 text-center py-10">Caricamento...</div>;

  const F = [
    { key: "carlo", name: "Carlo de Caprio" },
    { key: "antonio", name: "Antonio Micera" },
    { key: "tony", name: "Tony Rega" },
  ];

  return (
    <div className="space-y-6">
      <Section icon={UserSquare2} title="Foto e bio fondatori">
        <p className="text-white/60 text-sm">
          Le foto inserite qui sono mostrate nella sezione "Fondatori" della homepage. Caricale direttamente dal tuo dispositivo (JPEG/PNG/WEBP/GIF, max 8 MB), poi clicca "Salva fondatori".
        </p>
      </Section>

      <div className="grid md:grid-cols-3 gap-5">
        {F.map((f) => (
          <div key={f.key} className="p-5 rounded-2xl border border-white/10 bg-fp-surface/50 space-y-3" data-testid={`founder-card-${f.key}`}>
            <div className="aspect-square bg-fp-bg rounded-xl overflow-hidden border border-white/10 flex items-center justify-center">
              {s[`founder_${f.key}_img`] ? (
                <img src={resolveImageUrl(s[`founder_${f.key}_img`])} alt={f.name} className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="text-white/20" size={40} />
              )}
            </div>
            <div className="font-heading font-bold text-white">{f.name}</div>
            <Field label="Foto">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => uploadPhoto(f.key, e.target.files?.[0])}
                disabled={uploadingKey !== null}
                className="w-full text-sm text-white file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
                data-testid={`founder-${f.key}-img`}
              />
              {uploadingKey === f.key && <span className="text-xs text-fp-cyan">Caricamento...</span>}
            </Field>
            <Field label="Bio">
              <textarea
                rows={3}
                value={s[`founder_${f.key}_bio`] || ""}
                onChange={(e) => setS({ ...s, [`founder_${f.key}_bio`]: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-fp-bg/60 border border-white/10 text-white text-sm resize-none"
                data-testid={`founder-${f.key}-bio`}
              />
            </Field>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={loading || uploadingKey !== null}
        className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold tracking-wider uppercase text-sm hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all disabled:opacity-50"
        data-testid="save-founders-btn"
      >
        <Save size={16} /> {loading ? "Salvataggio..." : "Salva fondatori"}
      </button>
    </div>
  );
}

function Consensi() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = filter ? `/admin/consent-logs?consent_type=${filter}` : "/admin/consent-logs";
      const r = await adminApi().get(url);
      setItems(r.data || []);
    } catch { toast.error("Errore caricamento"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6">
      <Section icon={ShieldCheck} title="Registro consensi GDPR">
        <p className="text-white/60 text-sm">
          Archivio delle accettazioni cookie e termini/privacy degli utenti. Ai sensi del principio
          di accountability (art. 7 GDPR) conservato per 5 anni. Esportabile per audit.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {[
            { id: "", label: "Tutti" },
            { id: "cookie", label: "Cookie" },
            { id: "registration", label: "Iscrizione" },
          ].map((f) => (
            <button
              key={f.id || "all"}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-xs font-heading font-semibold tracking-wider uppercase ${
                filter === f.id ? "bg-fp-cyan text-fp-bg" : "border border-white/10 text-white/70 hover:border-fp-cyan/50"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button onClick={load} className="ml-auto p-2 rounded-lg border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan">
            <RefreshCw size={14} />
          </button>
        </div>
      </Section>

      <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
        {loading ? <div className="text-white/50 text-center py-6">Caricamento...</div> :
          items.length === 0 ? <div className="text-white/50 text-center py-10">Nessun consenso registrato</div> :
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="consent-log-table">
              <thead className="text-white/50 text-[10px] tracking-[0.3em] uppercase font-heading">
                <tr className="border-b border-white/10">
                  <th className="text-left px-3 py-3">Data</th>
                  <th className="text-left px-3 py-3">Tipo</th>
                  <th className="text-left px-3 py-3">Scelta</th>
                  <th className="text-left px-3 py-3">Email</th>
                  <th className="text-left px-3 py-3">IP</th>
                  <th className="text-left px-3 py-3">User Agent</th>
                  <th className="text-left px-3 py-3">Doc</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-white/5 hover:bg-fp-cyan/5">
                    <td className="px-3 py-2 text-white/70 whitespace-nowrap">{new Date(it.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-md bg-fp-cyan/10 text-fp-cyan text-[10px] uppercase font-heading font-bold tracking-wider">{it.consent_type}</span></td>
                    <td className="px-3 py-2 text-white/80">{it.choice || (it.privacy_accepted ? "✓ Privacy+Termini+Età" : "—")}</td>
                    <td className="px-3 py-2 text-white/60">{it.email || "—"}</td>
                    <td className="px-3 py-2 text-white/50 font-mono">{it.ip || "—"}</td>
                    <td className="px-3 py-2 text-white/40 max-w-xs truncate">{it.user_agent || "—"}</td>
                    <td className="px-3 py-2 text-white/40">{it.document_version || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  );
}

function PubblicitaAdmin() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", subtitle: "", order: 0, active: true });

  const load = async () => {
    try { const r = await adminApi().get("/admin/pubblicita"); setItems(r.data || []); }
    catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const onFilesChosen = (e) => {
    const list = Array.from(e.target.files || []);
    const bad = list.filter((f) => !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type));
    if (bad.length > 0) { toast.error("Formato non supportato: " + bad.map((f) => f.name).join(", ")); return; }
    const tooBig = list.filter((f) => f.size > 8 * 1024 * 1024);
    if (tooBig.length > 0) { toast.error("File troppo grandi (max 8 MB): " + tooBig.map((f) => f.name).join(", ")); return; }
    setFiles(list);
  };

  const uploadBatch = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const maxOrder = items.reduce((m, it) => Math.max(m, it.order || 0), 0);
    let ok = 0, ko = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res((r.result || "").toString().split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        await adminApi().post("/admin/pubblicita/upload", {
          filename: f.name,
          mime_type: f.type,
          image_b64: b64,
          title: "",
          subtitle: "",
          order: maxOrder + i + 1,
          active: true,
        }, { timeout: 120000 });
        ok += 1;
      } catch (e) { ko += 1; console.error("pubblicita upload err", f.name, e); }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    if (ko > 0) toast.error(`${ko} locandine non caricate. ${ok} caricate.`);
    else toast.success(`${ok} locandine caricate!`);
    setFiles([]);
    const el = document.getElementById("pubblicita-files-input");
    if (el) el.value = "";
    load();
  };

  const removeFileFromQueue = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const moveFile = (idx, dir) => setFiles((prev) => {
    const arr = [...prev]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; return arr;
  });

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditForm({
      title: it.title || "",
      subtitle: it.subtitle || "",
      order: it.order || 0,
      active: it.active !== false,
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    try {
      await adminApi().put(`/admin/pubblicita/${editingId}`, {
        title: editForm.title,
        subtitle: editForm.subtitle,
        order: Number(editForm.order || 0),
        active: !!editForm.active,
      });
      toast.success("Aggiornato");
      setEditingId(null);
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const toggleActive = async (it) => {
    try {
      await adminApi().put(`/admin/pubblicita/${it.id}`, { active: !(it.active !== false) });
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questa locandina?")) return;
    try { await adminApi().delete(`/admin/pubblicita/${id}`); load(); } catch { toast.error("Errore"); }
  };

  const formatBytes = (b) => !b ? "" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6">
      <Section icon={Megaphone} title="Pubblicità & Locandine">
        <p className="text-white/60 text-sm">
          Le locandine caricate qui vengono mostrate in un carosello prominente subito sotto l'Hero della homepage.
          Formati ammessi: <strong>JPEG, PNG, WEBP, GIF</strong> (max 8 MB ciascuna).
          Usa il campo <strong>Ordine</strong> per decidere la sequenza. Puoi disattivare temporaneamente una locandina senza eliminarla.
        </p>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Seleziona locandine</label>
          <input
            id="pubblicita-files-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={onFilesChosen}
            disabled={uploading}
            className="mt-2 w-full text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
            data-testid="pubblicita-files-input"
          />
        </div>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="pubblicita-files-queue">
            <div className="text-xs text-white/60 flex items-center justify-between">
              <span>{files.length} locandine pronte da caricare:</span>
              <button onClick={() => setFiles([])} className="text-red-300 hover:underline">Svuota</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative group rounded-xl overflow-hidden border border-white/10 bg-fp-bg/60">
                  <div className="aspect-square bg-fp-bg flex items-center justify-center overflow-hidden">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute top-1 left-1 px-2 py-1 rounded-full bg-fp-cyan text-fp-bg text-[10px] font-heading font-black">#{i + 1}</div>
                  <button onClick={() => removeFileFromQueue(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 size={12} />
                  </button>
                  <div className="p-2 text-[10px] text-white/70 truncate" title={f.name}>{f.name}</div>
                  <div className="px-2 pb-2 text-[9px] text-white/40 flex items-center justify-between">
                    <span>{formatBytes(f.size)}</span>
                    <div className="flex gap-1">
                      <button onClick={() => moveFile(i, -1)} disabled={i === 0} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↑</button>
                      <button onClick={() => moveFile(i, 1)} disabled={i === files.length - 1} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↓</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {uploading && (
              <div className="mt-3">
                <div className="text-xs text-white/60 mb-2 font-mono">Caricamento {progress.done}/{progress.total}</div>
                <div className="h-2 bg-fp-bg/60 rounded-full overflow-hidden">
                  <div className="h-full bg-fp-cyan transition-all" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
                </div>
              </div>
            )}
            <div className="flex justify-end pt-3">
              <button
                onClick={uploadBatch}
                disabled={uploading || files.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
                data-testid="pubblicita-upload-btn"
              >
                <Upload size={16} /> {uploading ? "Caricamento..." : `Carica ${files.length} locandine`}
              </button>
            </div>
          </div>
        )}
      </Section>

      <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h3 className="font-heading font-bold text-xl mb-4">Locandine attuali ({items.length})</h3>
        {items.length === 0 ? (
          <div className="text-white/50 text-center py-8">Nessuna locandina caricata</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {items.map((it) => (
              <div key={it.id} className={`rounded-xl border overflow-hidden transition-all ${it.active !== false ? "border-white/10 bg-fp-bg/40" : "border-white/5 bg-fp-bg/20 opacity-60"}`} data-testid={`pubblicita-item-${it.id}`}>
                <div className="aspect-video bg-fp-bg overflow-hidden">
                  <img src={resolveImageUrl(it.image_url)} alt={it.title || ""} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-2">
                  {editingId === it.id ? (
                    <>
                      <input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        placeholder="Titolo (opzionale)"
                        className="w-full px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                        data-testid={`pubblicita-edit-title-${it.id}`}
                      />
                      <input
                        value={editForm.subtitle}
                        onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                        placeholder="Descrizione (opzionale)"
                        className="w-full px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                        data-testid={`pubblicita-edit-subtitle-${it.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editForm.order}
                          onChange={(e) => setEditForm({ ...editForm, order: e.target.value })}
                          placeholder="Ordine"
                          className="w-24 px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                          data-testid={`pubblicita-edit-order-${it.id}`}
                        />
                        <label className="text-xs text-white/70 inline-flex items-center gap-1">
                          <input type="checkbox" checked={!!editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
                          Attiva
                        </label>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={saveEdit} className="flex-1 px-2 py-1.5 rounded-md bg-fp-cyan text-fp-bg font-heading font-bold text-xs inline-flex items-center justify-center gap-1" data-testid={`pubblicita-save-${it.id}`}>
                          <Save size={12} /> Salva
                        </button>
                        <button onClick={cancelEdit} className="px-2 py-1.5 rounded-md border border-white/10 text-white/70 text-xs">Annulla</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-white font-heading font-semibold truncate">{it.title || "(senza titolo)"}</div>
                      {it.subtitle && <div className="text-xs text-white/50 line-clamp-2">{it.subtitle}</div>}
                      <div className="text-[10px] text-white/40 flex items-center gap-2">
                        <span>Ordine: {it.order || 0}</span>
                        <span>·</span>
                        <span className={it.active !== false ? "text-fp-cyan" : "text-red-300"}>
                          {it.active !== false ? "Attiva" : "Nascosta"}
                        </span>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => startEdit(it)} className="flex-1 px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs inline-flex items-center justify-center gap-1" data-testid={`pubblicita-edit-${it.id}`}>
                          <Pencil size={12} /> Modifica
                        </button>
                        <button onClick={() => toggleActive(it)} className="px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs" data-testid={`pubblicita-toggle-${it.id}`} title={it.active !== false ? "Nascondi" : "Mostra"}>
                          <Eye size={12} />
                        </button>
                        <button onClick={() => del(it.id)} className="px-2 py-1.5 rounded-md text-red-400 hover:bg-red-500/10 text-xs" data-testid={`pubblicita-delete-${it.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function PremiAdmin() {
  const [items, setItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", subtitle: "", order: 0, active: true });

  const load = async () => {
    try { const r = await adminApi().get("/admin/prizes"); setItems(r.data || []); }
    catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const onFilesChosen = (e) => {
    const list = Array.from(e.target.files || []);
    const bad = list.filter((f) => !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(f.type));
    if (bad.length > 0) { toast.error("Formato non supportato: " + bad.map((f) => f.name).join(", ")); return; }
    const tooBig = list.filter((f) => f.size > 8 * 1024 * 1024);
    if (tooBig.length > 0) { toast.error("File troppo grandi (max 8 MB): " + tooBig.map((f) => f.name).join(", ")); return; }
    setFiles(list);
  };

  const uploadBatch = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const maxOrder = items.reduce((m, it) => Math.max(m, it.order || 0), 0);
    let ok = 0, ko = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const b64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res((r.result || "").toString().split(",")[1] || "");
          r.onerror = rej;
          r.readAsDataURL(f);
        });
        await adminApi().post("/admin/prizes/upload", {
          filename: f.name,
          mime_type: f.type,
          image_b64: b64,
          title: "",
          subtitle: "",
          order: maxOrder + i + 1,
          active: true,
        }, { timeout: 120000 });
        ok += 1;
      } catch (e) { ko += 1; console.error("prize upload err", f.name, e); }
      setProgress({ done: i + 1, total: files.length });
    }
    setUploading(false);
    if (ko > 0) toast.error(`${ko} premi non caricati. ${ok} caricati.`);
    else toast.success(`${ok} premi caricati!`);
    setFiles([]);
    const el = document.getElementById("premi-files-input");
    if (el) el.value = "";
    load();
  };

  const removeFileFromQueue = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const moveFile = (idx, dir) => setFiles((prev) => {
    const arr = [...prev]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]]; return arr;
  });

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditForm({
      title: it.title || "",
      subtitle: it.subtitle || "",
      order: it.order || 0,
      active: it.active !== false,
    });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async () => {
    try {
      await adminApi().put(`/admin/prizes/${editingId}`, {
        title: editForm.title,
        subtitle: editForm.subtitle,
        order: Number(editForm.order || 0),
        active: !!editForm.active,
      });
      toast.success("Aggiornato");
      setEditingId(null);
      load();
    } catch { toast.error("Errore aggiornamento"); }
  };

  const toggleActive = async (it) => {
    try {
      await adminApi().put(`/admin/prizes/${it.id}`, { active: !(it.active !== false) });
      load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo premio?")) return;
    try { await adminApi().delete(`/admin/prizes/${id}`); load(); } catch { toast.error("Errore"); }
  };

  const formatBytes = (b) => !b ? "" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / (1024 * 1024)).toFixed(2)} MB`;

  return (
    <div className="space-y-6">
      <Section icon={Gift} title="Galleria Premi">
        <p className="text-white/60 text-sm">
          Le immagini dei premi caricate qui vengono mostrate nella pagina pubblica <strong>/premi/gallery</strong> accessibile dal pulsante "Scopri tutti gli altri premi" nella sezione Premi della homepage.
          Formati ammessi: <strong>JPEG, PNG, WEBP, GIF</strong> (max 8 MB ciascuna).
          Usa il campo <strong>Ordine</strong> per decidere la sequenza.
        </p>

        <div>
          <label className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">Seleziona immagini dei premi</label>
          <input
            id="premi-files-input"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            onChange={onFilesChosen}
            disabled={uploading}
            className="mt-2 w-full text-sm text-white file:mr-4 file:py-2.5 file:px-4 file:rounded-xl file:border-0 file:bg-fp-cyan file:text-fp-bg file:font-heading file:font-bold file:uppercase file:tracking-wider file:text-xs hover:file:bg-fp-cyan/80 file:cursor-pointer cursor-pointer disabled:opacity-50"
            data-testid="premi-files-input"
          />
        </div>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="premi-files-queue">
            <div className="text-xs text-white/60 flex items-center justify-between">
              <span>{files.length} premi pronti da caricare:</span>
              <button onClick={() => setFiles([])} className="text-red-300 hover:underline">Svuota</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {files.map((f, i) => (
                <div key={`${f.name}-${i}`} className="relative group rounded-xl overflow-hidden border border-white/10 bg-fp-bg/60">
                  <div className="aspect-square bg-fp-bg flex items-center justify-center overflow-hidden">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute top-1 left-1 px-2 py-1 rounded-full bg-fp-cyan text-fp-bg text-[10px] font-heading font-black">#{i + 1}</div>
                  <button onClick={() => removeFileFromQueue(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 size={12} />
                  </button>
                  <div className="p-2 text-[10px] text-white/70 truncate" title={f.name}>{f.name}</div>
                  <div className="px-2 pb-2 text-[9px] text-white/40 flex items-center justify-between">
                    <span>{formatBytes(f.size)}</span>
                    <div className="flex gap-1">
                      <button onClick={() => moveFile(i, -1)} disabled={i === 0} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↑</button>
                      <button onClick={() => moveFile(i, 1)} disabled={i === files.length - 1} className="px-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30">↓</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {uploading && (
              <div className="mt-3">
                <div className="text-xs text-white/60 mb-2 font-mono">Caricamento {progress.done}/{progress.total}</div>
                <div className="h-2 bg-fp-bg/60 rounded-full overflow-hidden">
                  <div className="h-full bg-fp-cyan transition-all" style={{ width: `${(progress.done / Math.max(progress.total, 1)) * 100}%` }} />
                </div>
              </div>
            )}
            <div className="flex justify-end pt-3">
              <button
                onClick={uploadBatch}
                disabled={uploading || files.length === 0}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-40 hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
                data-testid="premi-upload-btn"
              >
                <Upload size={16} /> {uploading ? "Caricamento..." : `Carica ${files.length} premi`}
              </button>
            </div>
          </div>
        )}
      </Section>

      <div className="p-6 rounded-3xl border border-white/10 bg-fp-surface/50">
        <h3 className="font-heading font-bold text-xl mb-4">Premi in galleria ({items.length})</h3>
        {items.length === 0 ? (
          <div className="text-white/50 text-center py-8">Nessun premio caricato</div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
            {items.map((it) => (
              <div key={it.id} className={`rounded-xl border overflow-hidden transition-all ${it.active !== false ? "border-white/10 bg-fp-bg/40" : "border-white/5 bg-fp-bg/20 opacity-60"}`} data-testid={`prize-item-${it.id}`}>
                <div className="aspect-[4/5] bg-fp-bg overflow-hidden">
                  <img src={resolveImageUrl(it.image_url)} alt={it.title || ""} className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-2">
                  {editingId === it.id ? (
                    <>
                      <input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        placeholder="Titolo (opzionale)"
                        className="w-full px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                        data-testid={`prize-edit-title-${it.id}`}
                      />
                      <input
                        value={editForm.subtitle}
                        onChange={(e) => setEditForm({ ...editForm, subtitle: e.target.value })}
                        placeholder="Descrizione (opzionale)"
                        className="w-full px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                        data-testid={`prize-edit-subtitle-${it.id}`}
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={editForm.order}
                          onChange={(e) => setEditForm({ ...editForm, order: e.target.value })}
                          placeholder="Ordine"
                          className="w-24 px-2 py-1.5 rounded-md bg-fp-bg/60 border border-white/10 text-white text-xs"
                          data-testid={`prize-edit-order-${it.id}`}
                        />
                        <label className="text-xs text-white/70 inline-flex items-center gap-1">
                          <input type="checkbox" checked={!!editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
                          Attivo
                        </label>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={saveEdit} className="flex-1 px-2 py-1.5 rounded-md bg-fp-cyan text-fp-bg font-heading font-bold text-xs inline-flex items-center justify-center gap-1" data-testid={`prize-save-${it.id}`}>
                          <Save size={12} /> Salva
                        </button>
                        <button onClick={cancelEdit} className="px-2 py-1.5 rounded-md border border-white/10 text-white/70 text-xs">Annulla</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-white font-heading font-semibold truncate">{it.title || "(senza titolo)"}</div>
                      {it.subtitle && <div className="text-xs text-white/50 line-clamp-2">{it.subtitle}</div>}
                      <div className="text-[10px] text-white/40 flex items-center gap-2">
                        <span>Ordine: {it.order || 0}</span>
                        <span>·</span>
                        <span className={it.active !== false ? "text-fp-cyan" : "text-red-300"}>
                          {it.active !== false ? "Attivo" : "Nascosto"}
                        </span>
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => startEdit(it)} className="flex-1 px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs inline-flex items-center justify-center gap-1" data-testid={`prize-edit-${it.id}`}>
                          <Pencil size={12} /> Modifica
                        </button>
                        <button onClick={() => toggleActive(it)} className="px-2 py-1.5 rounded-md border border-white/10 text-white/70 hover:border-fp-cyan hover:text-fp-cyan text-xs" data-testid={`prize-toggle-${it.id}`} title={it.active !== false ? "Nascondi" : "Mostra"}>
                          <Eye size={12} />
                        </button>
                        <button onClick={() => del(it.id)} className="px-2 py-1.5 rounded-md text-red-400 hover:bg-red-500/10 text-xs" data-testid={`prize-delete-${it.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export default function AdminDashboardPage() {
  const nav = useNavigate();
  const [tab, setTab] = useState("iscritti");

  useEffect(() => {
    if (!localStorage.getItem("fp_admin_token")) nav("/admin/login");
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("fp_admin_token");
    localStorage.removeItem("fp_admin_user");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-fp-bg" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-fp-surface/40 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="w-10 h-10" />
            <div>
              <div className="font-heading font-black silver-gradient">FANTA PARTENOPE</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan">Admin Panel</div>
            </div>
          </Link>
          <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-white/70 hover:border-red-500/50 hover:text-red-400 text-sm" data-testid="admin-logout-btn">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-heading font-black text-3xl md:text-4xl silver-gradient mb-8">Dashboard</h1>
        <Tabs tab={tab} setTab={setTab} />
        {tab === "iscritti" && <Iscritti />}
        {tab === "classifiche" && <Classifiche />}
        {tab === "statistiche" && <Statistiche />}
        {tab === "gallery" && <Gallery />}
        {tab === "premi" && <PremiAdmin />}
        {tab === "pubblicita" && <PubblicitaAdmin />}
        {tab === "trailer" && <TrailerAdmin />}
        {tab === "regolamento" && <RegolamentoAdmin />}
        {tab === "sponsor" && <SponsorAdmin />}
        {tab === "fondatori" && <FoundersAdmin />}
        {tab === "messaggi" && <Messaggi />}
        {tab === "consensi" && <Consensi />}
        {tab === "impostazioni" && <Impostazioni />}
      </main>
    </div>
  );
}
